import React from "react";
import { Alert, RightDrawer } from "@coherehealth/common";
import { makeStyles } from "tss-react/mui";
import { H3 } from "@coherehealth/design-system";
import { UserManagementResponse } from "@coherehealth/qm-api";
import { GetDataError } from "restful-react";

interface UploadCompleteDrawerProps {
  open: boolean;
  handleClose: () => void;
  userManagementResponse: UserManagementResponse;
  transactionError: GetDataError<unknown> | null;
}

const useStyles = makeStyles()((theme) => ({
  statementWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: theme.spacing(2),
  },
  header: {
    lineHeight: 26 / 24,
    fontFamily: "Figtree",
    fontWeight: 600,
  },
  errorNotification: {
    ".MuiAlert-message": {
      color: theme.palette.error.main,
      fontFamily: "Figtree",
      fontWeight: 600,
    },
  },
  successNotification: {
    width: "100%",
    ".MuiAlert-message": {
      color: theme.palette.success.dark,
      fontFamily: "Figtree",
      fontWeight: 600,
    },
  },
  infoNotification: {
    ".MuiAlert-message": {
      color: theme.palette.info.dark,
      fontFamily: "Figtree",
      fontWeight: 600,
    },
  },
}));

export const UploadCompleteDrawer: React.FC<UploadCompleteDrawerProps> = (props) => {
  const { handleClose, open, transactionError, userManagementResponse } = props;
  const { classes } = useStyles();
  const {
    createCount,
    updateCount,
    deleteCount,
    userNamesWithInvalidStateName,
    userNamesWithInvalidSkillSet,
    userNamesWithInvalidQueueName,
    invalidStateNames,
    invalidSkillSetNames,
    invalidQueueNames,
  } = userManagementResponse;

  const totalUpdates = (createCount ?? 0) + (updateCount ?? 0) + (deleteCount ?? 0);

  const getUserCaption = (count: number) => {
    if (count === 1) {
      return "user was";
    }
    return "users were";
  };

  const getDontExistCaption = (count: number) => {
    if (count === 1) {
      return "does not exist";
    }
    return "do not exist";
  };

  return (
    <RightDrawer open={open} onClose={handleClose} buttonText={"Close"} useValidationFormat={false}>
      <div className={classes.statementWrapper}>
        <H3 className={classes.header}>{!!transactionError ? "Upload incomplete" : "Upload complete"}</H3>

        {transactionError && (
          <Alert severity="error" className={classes.errorNotification}>
            {(transactionError.data as GetDataError<unknown>)?.message}
          </Alert>
        )}

        {totalUpdates > 0 && (
          <Alert severity="success" className={classes.successNotification}>
            {`${totalUpdates} ${getUserCaption(totalUpdates)} uploaded`}
          </Alert>
        )}

        {!!userNamesWithInvalidStateName?.length && (
          <Alert severity="info" className={classes.infoNotification}>
            <>
              {`${userNamesWithInvalidStateName.length} ${getUserCaption(
                userNamesWithInvalidStateName.length
              )} not added because ${invalidStateNames?.join(", ")} is not a listed state.`}
              <br />
              <br />
              User IDs: {userNamesWithInvalidStateName.join(", ")}
            </>
          </Alert>
        )}

        {!!userNamesWithInvalidSkillSet?.length && (
          <Alert severity="info" className={classes.infoNotification}>
            <>
              {`${userNamesWithInvalidSkillSet.length} ${getUserCaption(userNamesWithInvalidSkillSet.length)} not added
              because the skillset: ${invalidSkillSetNames?.join(", ")} ${getDontExistCaption(
                invalidSkillSetNames?.length ?? 0
              )}.`}
              <br />
              <br />
              User IDs: {userNamesWithInvalidSkillSet.join(", ")}
            </>
          </Alert>
        )}

        {!!userNamesWithInvalidQueueName?.length && (
          <Alert severity="info" className={classes.infoNotification}>
            <>
              {`${userNamesWithInvalidQueueName.length} ${getUserCaption(
                userNamesWithInvalidQueueName.length
              )} not added because the queue: ${invalidQueueNames?.join(", ")} ${getDontExistCaption(
                invalidQueueNames?.length ?? 0
              )}.`}
              <br />
              <br />
              User IDs: {userNamesWithInvalidQueueName.join(", ")}
            </>
          </Alert>
        )}
      </div>
    </RightDrawer>
  );
};
